/**
 * Reservation Summary
 */
(() => {
  if (customElements.get('reservation-summary')) return;

  const HINT_LOADING = 'Checking availability…';
  const HINT_CONSENT = 'Please accept the House Rules, Terms of Use and SMS consent to continue.';
  const HINT_SUCCESS = 'Reservation made successfully.';
  const SUCCESS_HINT_DURATION_MS = 2500;

  const LINE_SPAN_CLASS = {
    title: 'reservation-summary__line-title font-text-sm-semibold text-color-primary',
    note: 'font-text-sm-regular text-color-secondary',
    sep: 'reservation-summary__sep font-text-sm-regular text-color-primary',
    price: 'reservation-summary__line-price font-text-sm-semibold text-color-primary',
    'datetime-date': 'font-text-sm-regular text-color-primary',
    'datetime-time': 'font-text-sm-regular text-color-secondary'
  };

  class ReservationSummary extends HTMLElement {
    constructor() {
      super();
      this.section = this.dataset.section;
      this.variant = this.dataset.variant;
      this.isLeague = this.dataset.league === 'true';
      this.basePrice = parseInt(this.dataset.basePrice, 10) || 0;
      this.currentVariantId = parseInt(this.dataset.selectedVariantId, 10) || 0;

      this.rateRange = null;
      this.membershipInfo = null;

      this.suitesTarget = this.querySelector('[data-reservation-summary-suites]');
      this.hoursTarget = this.querySelector('[data-reservation-summary-hours]');
      this.basePriceTarget = this.querySelector('[data-reservation-summary-base-price]');
      this.addonsTarget = this.querySelector('[data-reservation-summary-addons]');
      this.promoTarget = this.querySelector('[data-reservation-summary-promo]');
      this.tierTarget = this.querySelector('[data-reservation-summary-tier]');
      this.instructorTarget = this.querySelector('[data-reservation-summary-instructor]');
      this.membershipTarget = this.querySelector('[data-reservation-summary-membership]');
      this.membershipTextTarget = this.querySelector('[data-reservation-summary-membership-text]');
      this.totalTarget = this.querySelector('[data-reservation-summary-total]');
      this.rateNoteTarget = this.querySelector('[data-reservation-summary-rate-note]');
      this.rateWeekdayTarget = this.querySelector('[data-reservation-summary-rate-weekday]');
      this.rateWeekendTarget = this.querySelector('[data-reservation-summary-rate-weekend]');

      this.reserveButton = this.querySelector('[data-reservation-summary-reserve]');
      this.consentInputs = Array.from(this.querySelectorAll('[data-reservation-summary-consent]'));

      this.hint = this.querySelector('[data-reservation-summary-hint]');
      this.hintText = this.hint ? this.hint.querySelector('span') : null;
      this.defaultHint = this.hintText ? this.hintText.textContent.trim() : '';
      this.datetimeTarget = this.querySelector('[data-reservation-summary-datetime]');

      this.bookingState = { hasSelection: false, slotConflict: false, loading: false, date: null, time: null };
      this.bookingController = null;

      this._suppressHint = false;
      this._successTimer = null;

      this.recompute = this.recompute.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.handleConsentChange = this.handleConsentChange.bind(this);
      this.handleInstructorChange = this.handleInstructorChange.bind(this);
      this.handleReserveClick = this.handleReserveClick.bind(this);
    }

    connectedCallback() {
      this.initConsentGating();
      this.initPricing();
    }

    disconnectedCallback() {
      clearTimeout(this._successTimer);
      if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
      document.removeEventListener('variant:change', this.handleVariantChange);
      document.removeEventListener('club-rental:change', this.recompute);
      document.removeEventListener('membership:preference', this.recompute);
      document.removeEventListener('change', this.handleInstructorChange);
      this.removeEventListener('change', this.handleConsentChange);
      if (this.reserveButton) this.reserveButton.removeEventListener('click', this.handleReserveClick);
    }

    initConsentGating() {
      if (!this.reserveButton) return;

      this.addEventListener('change', this.handleConsentChange);
      this.reserveButton.addEventListener('click', this.handleReserveClick);
      this.updateReserveState();
    }

    handleConsentChange(event) {
      if (!event.target.matches('[data-reservation-summary-consent]')) return;
      this.updateReserveState();
    }

    handleReserveClick() {
      if (this.reserveButton.disabled) return;
      if (this.bookingController && typeof this.bookingController.onReserve === 'function') {
        this.bookingController.onReserve();
      }
    }

    consentSatisfied() {
      return this.consentInputs.length === 0 || this.consentInputs.every((input) => input.checked);
    }

    setBookingState(state) {
      this.bookingState = Object.assign({}, this.bookingState, state);
      this.updateReserveState();
      this.renderDatetime();
    }

    renderDatetime() {
      if (!this.datetimeTarget) return;

      const { hasSelection, date, time } = this.bookingState;
      if (!hasSelection || !date || !time) {
        this.datetimeTarget.textContent = '';
        this.datetimeTarget.hidden = true;
        return;
      }

      this.datetimeTarget.textContent = '';
      this.datetimeTarget.appendChild(this.buildSpan({ role: 'datetime-date', text: date }));
      this.datetimeTarget.appendChild(this.buildSpan({ role: 'sep', text: '·' }));
      this.datetimeTarget.appendChild(this.buildSpan({ role: 'datetime-time', text: time }));
      this.datetimeTarget.hidden = false;
    }

    updateReserveState() {
      if (!this.reserveButton) return;

      const consentOk = this.consentSatisfied();
      const canReserve = consentOk
        && this.bookingState.hasSelection
        && !this.bookingState.slotConflict
        && !this.bookingState.loading;

      this.reserveButton.disabled = !canReserve;
      this.reserveButton.setAttribute('aria-disabled', canReserve ? 'false' : 'true');
      this.classList.toggle('_disabled', !canReserve);
      this.updateHint(consentOk);
    }

    updateHint(consentOk) {
      if (!this.hint || !this.hintText || this._suppressHint) return;

      let message = '';
      if (this.bookingState.loading) {
        message = HINT_LOADING;
      } else if (!this.bookingState.hasSelection) {
        message = this.defaultHint;
      } else if (!consentOk) {
        message = HINT_CONSENT;
      }

      this.hintText.textContent = message;
      this.hint.classList.toggle('_disabled', message !== '');
      this.hint.classList.remove('_error');
    }

    showError(message) {
      if (!this.hint || !this.hintText) return;
      this.hintText.textContent = message;
      this.hint.classList.add('_disabled');
      this.hint.classList.add('_error');
    }

    resetConsent() {
      this.consentInputs.forEach((input) => {
        input.checked = input.defaultChecked;
      });
      this.updateReserveState();
    }

    showSuccess(message) {
      if (!this.hint || !this.hintText) return;
      this._suppressHint = true;
      this.hintText.textContent = message || HINT_SUCCESS;
      this.hint.classList.remove('_disabled', '_error');
      this.hint.classList.add('_success');

      clearTimeout(this._successTimer);
      this._successTimer = setTimeout(() => {
        this._suppressHint = false;
        this.hint.classList.remove('_success');
        this.updateReserveState();
      }, SUCCESS_HINT_DURATION_MS);
    }

    initPricing() {
      document.addEventListener('membership:preference', this.recompute);
      document.addEventListener('variant:change', this.handleVariantChange);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId !== this.section) return;
          this.applyVariant(event.data.variant);
        });
      }

      document.addEventListener('club-rental:change', this.recompute);
      document.addEventListener('change', this.handleInstructorChange);

      this.recompute();
    }

    handleVariantChange(event) {
      this.applyVariant(event.detail && event.detail.variant);
    }

    /** Keep the last real price if the new variant cannot be resolved. */
    applyVariant(variant) {
      if (!variant || !variant.id) return;
      if (variant.id === this.currentVariantId) return;

      // variant:change has no section id — ignore other products on the page.
      const variants = this.getRateVariants();
      if (variants.length && !variants.some((entry) => entry.id === variant.id)) return;

      this.currentVariantId = variant.id;
      this.basePrice = this.isLeague ? 0 : variant.price;
      this.recompute();
    }

    handleInstructorChange(event) {
      if (!event.target.matches('.product-form__input-instructor')) return;
      this.recompute();
    }

    async recompute() {
      // One context for the whole pass — resolving it per renderer would redo the slot and promo lookups.
      this.pricingContext = this.readSlotPricingContext();

      this.renderLabels();
      this.rateRange = this.resolveRateRange();
      this.membershipInfo = this.getMembershipInfo();

      const basePrice = await this.getDiscountedBasePrice();
      // Hours that actually reduce the price replace the range: a member being quoted
      // "$35.00 – $40.00" while the line below says their hours apply contradicts itself.
      // A member with nothing left pays the day's rate like everyone, so their range is honest.
      this.hoursReducePrice = basePrice < this.basePrice;
      const addons = this.getAddonLines();
      const instructor = this.getInstructorLine();
      const promo = this.getPromoInfo();
      const tier = this.getTierInfo();

      const addonsTotal = addons.reduce((sum, addon) => sum + addon.price, 0);
      const instructorTotal = instructor ? instructor.price : 0;

      this.renderBasePrice(basePrice);
      this.renderAddons(addons);
      const tierShowing = this.renderTier(tier);
      this.renderPromo(promo);
      this.renderInstructor(instructor);
      this.renderMembership(this.isMembershipNamingHeld() ? null : this.membershipInfo);

      const extras = addonsTotal + instructorTotal;
      const promoOff = promo ? promo.offCents : 0;
      const tierOff = tierShowing ? tier.offCents : 0;
      // Promo and tier reduce the reservation line only — never let the clamp eat add-ons.
      this.renderTotal(Math.max(0, basePrice - tierOff - promoOff) + extras, extras);
      this.renderRateNote();
    }

    getPromoInfo() {
      if (this.isLeague || this.shouldShowRange()) return null;
      const context = this.getSlotPricingContext();
      return context && context.hasContext ? context.promoResult : null;
    }

    /** The member's tier percent on what the hours did not cover — its own row, like the promo. */
    getTierInfo() {
      if (this.variant !== 'multi') return null;
      const context = this.getSlotPricingContext();
      const tier = context && context.hasContext ? context.tierResult : null;
      return tier && tier.offCents > 0 ? tier : null;
    }

    /** Returns whether the row is showing — the total only deducts what it can also explain. */
    renderTier(tier) {
      if (!this.tierTarget) return false;
      this.tierTarget.textContent = '';
      // Without the locale copy the row cannot explain itself, so it is not rendered at all.
      if (!tier || !this.membershipCopy) return false;

      const label = this.membershipCopy.text('tierDiscount', { percent: tier.percent, plan: tier.label });
      this.tierTarget.appendChild(
        this.buildLine('reservation-summary__line--tier', [{ role: 'title', text: label }], -tier.offCents)
      );
      return true;
    }

    renderPromo(promo) {
      if (!this.promoTarget) return;
      this.promoTarget.textContent = '';
      if (!promo) return;
      this.promoTarget.appendChild(
        this.buildLine('reservation-summary__line--promo', [{ role: 'title', text: promo.message }], -promo.offCents)
      );
    }

    async getDiscountedBasePrice() {
      if (this.variant === 'multi') {
        const context = this.getSlotPricingContext();
        return context && context.hasContext ? context.finalPriceCents : this.basePrice;
      }

      if (typeof window.getMembershipDiscountedPrice === 'function') {
        try {
          return await window.getMembershipDiscountedPrice(this.basePrice);
        } catch (error) {
          console.error('Reservation summary: membership price lookup failed', error);
        }
      }
      return this.basePrice;
    }

    /** Context from this recompute() pass; falls back to a fresh read if recompute has not run. */
    getSlotPricingContext() {
      return typeof this.pricingContext === 'undefined' ? this.readSlotPricingContext() : this.pricingContext;
    }

    readSlotPricingContext() {
      return this.bookingController && typeof this.bookingController.getSlotPricingContext === 'function'
        ? this.bookingController.getSlotPricingContext()
        : null;
    }

    getMembershipConfig() {
      if (typeof this._membershipConfig !== 'undefined') return this._membershipConfig;

      this._membershipConfig = null;
      const node = document.getElementById('membership-discount-data');
      if (!node) return this._membershipConfig;

      try {
        this._membershipConfig = JSON.parse(node.textContent);
      } catch (error) {
        console.error('Reservation summary: membership config parse failed', error);
      }
      return this._membershipConfig;
    }

    getMembershipInfo() {
      if (this.isLeague) return null;
      if (this.variant === 'multi') return this.getCreditInfoForSelectedSlot() || this.getMembershipInfoForSelectedSlot();
      return this.getMembershipInfoForToday() || this.getCreditInfoForToday();
    }

    /**
     * Hour credit, when the membership paid nothing. Before a time is picked the A9OS grants
     * only say it applies — the backend alone knows how many hours a slot gets.
     */
    getCreditInfoForSelectedSlot() {
      const context = this.getSlotPricingContext();
      const credit = context && context.hasContext ? context.creditResult : null;
      const copy = this.membershipCopy;
      if (!credit || !copy) return null;

      if (credit.isEstimate) {
        return { type: 'credit', text: copy.text(credit.weekdaysOnly ? 'creditAppliesWeekdays' : 'creditApplies') };
      }

      const partialText = credit.paidHours > 0 ? ` · ${copy.text('alsoBilled', { hours: this.hoursText(credit.paidHours) })}` : '';
      return { type: 'credit', text: `${copy.text('creditAppliedHours', { hours: this.hoursText(credit.hours) })}${partialText}.` };
    }

    /** The page preview: the date is picked in the popup, so any grant still valid here counts. */
    getCreditInfoForToday() {
      const credit = window.A9HourCredit;
      const config = this.getMembershipConfig();
      const copy = this.membershipCopy;
      if (!credit || !copy || !config || !config.facilityId) return null;

      const coverage = credit.coverageForFacility(config.hourCredits, Number(config.facilityId));
      if (!coverage.applies) return null;
      return { type: 'credit', text: copy.text(coverage.weekdaysOnly ? 'creditAppliesWeekdays' : 'creditApplies') };
    }

    isMembershipNamingHeld() {
      const facilityId = Number(this.getMembershipConfig()?.facilityId);
      return !!window.MembershipSlotPricing?.isMembershipPreferencePending?.(facilityId);
    }

    /** Membership copy resolver; all membership sentences go through locales. */
    get membershipCopy() {
      return window.MembershipStrings;
    }

    hoursText(hours) {
      return this.membershipCopy.hours('hours', hours);
    }

    planTitle(period) {
      const plans = window.MembershipPlans;
      return plans ? plans.titleFor(period) : this.membershipCopy.text('fallbackPlanName');
    }

    /** The plan's own limits in words, or '' when it has none. Days and window come from the plan. */
    planLimitsPhrase(period) {
      const plans = window.MembershipPlans;
      const copy = this.membershipCopy;
      if (!plans || !copy || !period) return '';
      const dailyLimit = plans.dailyLimitFor(period);
      if (dailyLimit == null) return '';

      const parts = [copy.text('limitPerDay', { hours: dailyLimit })];
      if (plans.forbidsWeekends(period)) parts.push(copy.text('weekdaysOnly'));
      const windowLabel = plans.windowLabelFor(period);
      if (windowLabel) parts.push(windowLabel);
      return parts.join(', ');
    }

    /**
     * " for today" / " for this date" / "" — which date the numbers describe.
     * Hours priced by the backend are not an estimate, so they carry no hedge.
     */
    membershipDateSuffix(context) {
      const copy = this.membershipCopy;
      if (!copy) return '';
      if (context.hoursSource === 'backend') return '';
      if (context.isTodayFallback) return ` ${copy.text('forToday')}`;
      return context.isEstimate ? ` ${copy.text('forThisDate')}` : '';
    }

    getMembershipInfoForSelectedSlot() {
      const context = this.getSlotPricingContext();
      if (!context || !context.hasContext) return null;

      const result = context.discountResult;
      const utils = window.MembershipHoursUtils;
      const plans = window.MembershipPlans;
      const copy = this.membershipCopy;
      if (!copy) return null;

      const suffix = this.membershipDateSuffix(context);
      const period = (result && result.membershipHoursUsedByPeriod[0] && result.membershipHoursUsedByPeriod[0].period)
        || context.coveringPeriod;

      if (!result || !result.canUseMembership) {
        const dailyLimit = plans && period ? plans.dailyLimitFor(period) : null;
        if (dailyLimit != null) {
          return {
            type: 'limit-reached',
            text: copy.text('dailyLimitReached', { plan: this.planTitle(period), hours: dailyLimit })
          };
        }
        return null;
      }

      const usedHours = utils ? utils.roundHours(result.membershipHoursUsed) : result.membershipHoursUsed;
      const paidHours = utils ? utils.roundHours(Math.max(0, result.paidHours)) : Math.max(0, result.paidHours);
      const partialText = paidHours > 0 ? ` · ${copy.text('alsoBilled', { hours: this.hoursText(paidHours) })}` : '';
      const limitsPhrase = this.planLimitsPhrase(period);
      const limitsText = limitsPhrase ? ` (${limitsPhrase})` : '';
      const tokens = { plan: this.planTitle(period), hours: this.hoursText(usedHours) };

      if (result.isUnlimited) {
        return {
          type: 'unlimited',
          text: `${copy.text('appliedFreeHours', tokens)}${suffix}${limitsText}${partialText}.`
        };
      }

      const config = this.bookingController.config;
      const endLabel = period
        ? this.getPeriodEndLabel(config.membership, period.facility_id, period.start, config.timeZone)
        : '';
      const untilText = endLabel ? ` (${copy.text('periodEnds', { date: endLabel })})` : '';

      return {
        type: 'pool',
        text: `${copy.text('appliedUsedHours', tokens)}${suffix}${untilText}${partialText}.`
      };
    }

    preferredMembershipType(facilityId) {
      const pricing = window.MembershipSlotPricing;
      return pricing?.resolvePreferredType?.(facilityId, null) || null;
    }

    /** Period end as a calendar day — parsing the UTC instant would shift it back a day. */
    getPeriodEndLabel(membership, facilityId, dateStr, tz) {
      const pricing = window.MembershipSlotPricing;
      const dateUtils = window.A9DateUtils;
      if (!pricing || !dateUtils || !dateStr) return '';

      const coverage = pricing.getMembershipCoverageForDate(membership, facilityId, dateStr, tz, this.preferredMembershipType(facilityId));
      return coverage.endYmd ? dateUtils.formatMdyLong(coverage.endYmd) : '';
    }

    getMembershipInfoForToday() {
      const utils = window.MembershipHoursUtils;
      const config = this.getMembershipConfig();
      if (!utils || !config || !config.membership || !config.facilityId) return null;

      const facilityId = Number(config.facilityId);
      const tz = config.facilityTimezone;
      if (!utils.hasMembershipSlotData(config.membership, facilityId)) return null;

      const copy = this.membershipCopy;
      if (!copy) return null;

      const info = utils.getMembershipHoursForToday(config.membership, facilityId, 0, tz, this.preferredMembershipType(facilityId));
      if (!info.period) return null;

      const limitsPhrase = this.planLimitsPhrase(info.period);

      if (!info.hasHourPool) {
        if (!limitsPhrase) return null;
        return {
          type: 'unlimited',
          text: copy.text('appliedIncludesLimits', { plan: this.planTitle(info.period), limits: limitsPhrase })
        };
      }

      if (info.hours == null) return null;

      const dateUtils = window.A9DateUtils;
      const todayYmd = dateUtils ? dateUtils.currentYmd(tz) : null;
      const endLabel = this.getPeriodEndLabel(config.membership, facilityId, todayYmd, tz);

      const remainingHours = utils.roundHours(Math.max(0, info.hours));
      const untilText = endLabel
        ? ` ${copy.text('remainingUntil', { date: endLabel })}`
        : ` ${copy.text('remainingThisPeriod')}`;
      const limitsText = limitsPhrase ? ` (${limitsPhrase})` : '';

      return {
        type: 'pool',
        text: `${copy.text('appliedRemainingHours', { plan: this.planTitle(info.period), hours: this.hoursText(remainingHours) })}${untilText}${limitsText}.`
      };
    }

    renderMembership(info) {
      if (!this.membershipTarget || !this.membershipTextTarget) return;

      if (!info) {
        this.membershipTarget.hidden = true;
        this.membershipTextTarget.textContent = '';
        return;
      }

      this.setText(this.membershipTextTarget, info.text);
      this.membershipTarget.hidden = false;
    }

    getAddonLines() {
      const lines = [];

      document.querySelectorAll('[data-club-rental-item]').forEach((item) => {
        const qtyInput = item.querySelector('[data-club-rental-qty]');
        const qty = parseInt(qtyInput?.value, 10) || 0;
        if (qty <= 0) return;

        const label = item.dataset.clubRentalTypeLabel || '';

        item.querySelectorAll('[data-club-rental-hand][aria-pressed="true"]').forEach((hand) => {
          const unitPrice = parseInt(hand.dataset.price, 10) || 0;
          lines.push({
            label,
            side: hand.dataset.hand || '',
            qty,
            price: unitPrice * qty
          });
        });
      });

      return lines;
    }

    getInstructorLine() {
      const input = document.querySelector('.product-form__input-instructor:checked');
      if (!input) return null;

      return {
        name: input.value,
        price: this.getInstructorPrice(input)
      };
    }

    getInstructorPrice(input) {
      const variantId = parseInt(input.dataset.optionValue, 10);
      const jsonElement = document.querySelector(`[data-for="${input.id}"]`);
      if (!variantId || !jsonElement) return 0;

      try {
        const instructor = JSON.parse(jsonElement.textContent || '{}');
        const variant = (instructor.variants || []).find((entry) => entry.id === variantId);
        return variant ? variant.price : 0;
      } catch (error) {
        console.error('Reservation summary: instructor price lookup failed', error);
        return 0;
      }
    }

    getSelectedOptionValue(slugs) {
      for (const slug of slugs) {
        const wrapper = document.querySelector(`[data-option-slug="${slug}"]`);
        if (!wrapper) continue;

        const checked = wrapper.querySelector('input:checked');
        if (checked) return checked.value;

        const select = wrapper.querySelector('select');
        if (select) return select.value;
      }
      return null;
    }

    renderLabels() {
      const hours = this.getSelectedOptionValue(['hours', 'hour']);
      if (hours != null && this.hoursTarget) {
        this.setText(this.hoursTarget, window.ReservationRateUtils.formatHours(hours));
      }

      if (!this.suitesTarget) return;
      let suites = '1';
      if (this.variant === 'multi') {
        suites = this.getSelectedOptionValue(['suites', 'suite']) || '1';
      }
      this.setText(this.suitesTarget, `${suites} ${this.pluralize(suites, 'Suite', 'Suites')}`);
    }

    pluralize(value, singular, plural) {
      return parseFloat(value) === 1 ? singular : plural;
    }

    setText(target, text) {
      if (target.textContent !== text) target.textContent = text;
    }

    /** Date picked (multi-suite only). On standard the date lives in the popup, so the range stays. */
    hasDatePicked() {
      if (this.variant !== 'multi') return false;
      const context = this.getSlotPricingContext();
      return !!(context && context.hasContext && !context.isTodayFallback);
    }

    /**
     * Members pay the same day rates as everyone, so the range itself is no longer hidden from
     * them — only a price their hours already reduced replaces it with the exact figure.
     */
    shouldShowRange() {
      return !!this.rateRange && !this.hasDatePicked() && !this.hoursReducePrice;
    }

    /** Variant rows for weekday/Fri–Sun rate lookup. */
    getRateVariants() {
      if (this._rateVariants) return this._rateVariants;

      this._rateVariants = [];
      const node = this.querySelector('[data-reservation-summary-variants]');
      if (!node) return this._rateVariants;

      try {
        this._rateVariants = JSON.parse(node.textContent) || [];
      } catch (error) {
        console.error('Reservation summary: variant rate data parse failed', error);
      }
      return this._rateVariants;
    }

    /** Weekday/Fri–Sun pair for the current duration + suites. Null rather than guess. */
    resolveRateRange() {
      const rateUtils = window.ReservationRateUtils;
      if (this.isLeague || !rateUtils) return null;

      const pair = rateUtils.resolveDayTypePair(this.getRateVariants(), this.currentVariantId);
      if (!pair || !pair.weekday || !pair.weekend || pair.hours <= 0) return null;
      if (pair.weekday.price <= 0 || pair.weekend.price <= 0) return null;
      if (pair.weekday.price === pair.weekend.price) return null;

      return { weekday: pair.weekday.price, weekend: pair.weekend.price, hours: pair.hours };
    }

    /** Per-hour reading of the range above. */
    renderRateNote() {
      if (!this.rateNoteTarget) return;

      const show = this.shouldShowRange();
      this.rateNoteTarget.hidden = !show;
      if (!show) return;

      const { weekday, weekend, hours } = this.rateRange;
      if (this.rateWeekdayTarget) this.setText(this.rateWeekdayTarget, `${this.formatMoney(weekday / hours)}/hr`);
      if (this.rateWeekendTarget) this.setText(this.rateWeekendTarget, `${this.formatMoney(weekend / hours)}/hr`);
    }

    /** Range including add-ons already on the reservation. */
    formatRange(extraCents = 0) {
      return this.formatMoney(this.rateRange.weekday + extraCents) +
        ' – ' + this.formatMoney(this.rateRange.weekend + extraCents);
    }

    renderBasePrice(price) {
      if (!this.basePriceTarget) return;
      this.setText(this.basePriceTarget, this.shouldShowRange() ? this.formatRange() : this.formatMoney(price));
    }

    renderAddons(addons) {
      if (!this.addonsTarget) return;
      this.addonsTarget.textContent = '';
      addons.forEach((addon) => this.addonsTarget.appendChild(this.buildAddonLine(addon)));
    }

    renderInstructor(instructor) {
      if (!this.instructorTarget) return;
      this.instructorTarget.textContent = '';
      if (instructor) this.instructorTarget.appendChild(this.buildInstructorLine(instructor));
    }

    renderTotal(total, extraCents = 0) {
      if (!this.totalTarget) return;
      this.setText(this.totalTarget, this.shouldShowRange() ? this.formatRange(extraCents) : this.formatMoney(total));
    }

    buildAddonLine(addon) {
      const parts = [{ role: 'title', text: addon.label }];
      if (addon.side) parts.push({ role: 'note', text: `(${addon.side})` });
      parts.push({ role: 'sep', text: 'x' });
      parts.push({ role: 'note', text: String(addon.qty) });
      return this.buildLine('reservation-summary__line--addon', parts, addon.price);
    }

    buildInstructorLine(instructor) {
      const parts = [
        { role: 'title', text: 'Instructor' },
        { role: 'sep', text: '·' },
        { role: 'note', text: instructor.name }
      ];
      return this.buildLine(null, parts, instructor.price);
    }

    buildLine(modifier, parts, price) {
      const line = document.createElement('div');
      line.className = 'reservation-summary__line';
      if (modifier) line.classList.add(modifier);

      const main = document.createElement('div');
      main.className = 'reservation-summary__line-main';

      const info = document.createElement('div');
      info.className = 'reservation-summary__line-info';
      parts.forEach((part) => info.appendChild(this.buildSpan(part)));

      main.appendChild(info);
      main.appendChild(this.buildSpan({ role: 'price', text: this.formatMoney(price) }));
      line.appendChild(main);
      return line;
    }

    buildSpan({ role, text }) {
      const span = document.createElement('span');
      span.className = LINE_SPAN_CLASS[role];
      span.textContent = text;
      if (role === 'sep') span.setAttribute('aria-hidden', 'true');
      return span;
    }

    /** A discount reads as −$5.40; the minus belongs before the symbol, not inside it. */
    formatMoney(cents) {
      const formatted = window.ReservationRateUtils.formatMoney(Math.abs(cents));
      return cents < 0 ? '−' + formatted : formatted;
    }
  }

  customElements.define('reservation-summary', ReservationSummary);
})();
